import { Box, Chip, ListItemButton, Paper, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { semanticTone } from 'components/@home/@settings/Tabs/Agents/agenticThemeHelpers';
import FileTreeOutlineIcon from 'mdi-react/FileTreeOutlineIcon';
import PuzzleOutlineIcon from 'mdi-react/PuzzleOutlineIcon';
import WrenchOutlineIcon from 'mdi-react/WrenchOutlineIcon';
import PropTypes from 'prop-types';
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { I18n } from 'utils/i18n';
import { localizeReferenceKind } from './referenceLabels';

const getStyles = theme => {
  const primaryTone = semanticTone(theme, 'primary');
  const mutedChip = semanticTone(theme, 'default');
  return {
    paper: {
      width: 300,
      maxHeight: 300,
      overflowY: 'auto',
      borderRadius: 1,
      boxShadow: theme.shadows[6],
      p: 0.5,
    },
    empty: {
      p: '12px 14px',
      fontSize: theme.typography.pxToRem(12.5),
      color: 'text.secondary',
    },
    item: {
      width: '100%',
      textAlign: 'left',
      p: '6px 10px',
      borderRadius: '6px',
      gap: 1,
      alignItems: 'flex-start',
      '&.Mui-selected': {
        backgroundColor: primaryTone.bg,
      },
      '&.Mui-selected:hover': {
        backgroundColor: primaryTone.bg,
      },
    },
    name: {
      fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
      fontSize: theme.typography.pxToRem(12.5),
      color: 'text.primary',
      fontWeight: 600,
    },
    desc: {
      fontSize: theme.typography.pxToRem(11.5),
      color: 'text.secondary',
      mt: '2px',
      lineHeight: 1.4,
    },
    kindBadge: {
      height: 18,
      ml: 'auto',
      flexShrink: 0,
      fontSize: theme.typography.pxToRem(10),
      fontWeight: 600,
      letterSpacing: '0.3px',
      color: 'text.secondary',
      backgroundColor: mutedChip.bg,
      borderRadius: '4px',
      textTransform: 'uppercase',
      '& .MuiChip-label': { px: 0.75, py: 0 },
    },
  };
};

const iconFor = kind => {
  if (kind === 'skill') return FileTreeOutlineIcon;
  if (kind === 'builtin') return PuzzleOutlineIcon;
  return WrenchOutlineIcon;
};

const MentionList = forwardRef(({ items, command }, ref) => {
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const primaryTone = useMemo(() => semanticTone(theme, 'primary'), [theme]);
  const [index, setIndex] = useState(0);

  useEffect(() => setIndex(0), [items]);

  const selectItem = i => {
    const it = items[i];
    if (!it) return;
    command({ id: it.name, label: it.name });
  };

  useImperativeHandle(ref, () => ({
    onKeyDown({ event }) {
      if (event.key === 'ArrowUp') {
        setIndex((index + items.length - 1) % Math.max(items.length, 1));
        return true;
      }
      if (event.key === 'ArrowDown') {
        setIndex((index + 1) % Math.max(items.length, 1));
        return true;
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        selectItem(index);
        return true;
      }
      return false;
    },
  }));

  return (
    <Paper sx={styles.paper}>
      {items.length === 0 ? (
        <Typography sx={styles.empty}>{I18n.t('AgenticChatFlow.MentionNoMatches')}</Typography>
      ) : (
        items.map((item, i) => {
          const Icon = iconFor(item.kind);
          return (
            <ListItemButton
              key={`${item.kind}-${item.name}`}
              sx={styles.item}
              selected={i === index}
              onMouseEnter={() => setIndex(i)}
              onMouseDown={e => {
                e.preventDefault();
                selectItem(i);
              }}
            >
              <Icon size={15} color={primaryTone.fg} style={{ marginTop: 2, flexShrink: 0 }} />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={styles.name}>@{item.name}</Typography>
                {item.description && (
                  <Typography sx={styles.desc} noWrap>
                    {item.description}
                  </Typography>
                )}
              </Box>
              <Chip size="small" sx={styles.kindBadge} label={localizeReferenceKind(item.kind)} />
            </ListItemButton>
          );
        })
      )}
    </Paper>
  );
});

MentionList.displayName = 'MentionList';

MentionList.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  command: PropTypes.func.isRequired,
};

export default MentionList;
